import { useMemo, useState } from 'react';
import Icon from './Icon';

/** Initials for the fallback, e.g. "Millennium BCP" -> "MB". */
const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?';

// Logo URLs that failed to load, shared by every instance: a long movement list renders one logo
// per row, and each would otherwise request the same missing file again.
const failedUrls = new Set<string>();

/**
 * Bank logo with graceful, layered fallback:
 *   1. self-hosted asset  /bank-logos/{code}.svg  or  /bank-logos/{code}.png
 *      (our own files — the only logo source for non-SIBS integrations like
 *      LusoPay / Revolut / Stripe / PayPal / cash funds, and an offline-safe
 *      override for SIBS banks)
 *   2. the provided external src (SIBS CDN URL), if any
 *   3. a branded initials avatar, or the `fallbackIcon` when given
 *
 * External bank-logo URLs sometimes 404 or render broken. This walks the
 * candidates on each onError and never shows a broken image. Each instance
 * tracks its own position, so it works inside lists.
 */
export default function BankLogo({
  code,
  src,
  name,
  className = '',
  fallbackIcon,
}: {
  /** Stable bank key (aspspCde / bankCode) used for the self-hosted asset. */
  code?: string | null;
  /** External logo URL, used if no self-hosted asset is present. */
  src?: string | null;
  name: string;
  /** Sizing/shape classes applied to both the image and the fallback. */
  className?: string;
  /** Icon shown instead of the initials when no logo loads (e.g. a generic bank). */
  fallbackIcon?: string;
}) {
  const candidates = useMemo(
    () =>
      [
        code ? `/bank-logos/${code}.svg` : null,
        code ? `/bank-logos/${code}.png` : null,
        src,
      ].filter((u): u is string => !!u && !failedUrls.has(u)),
    [code, src],
  );
  const [idx, setIdx] = useState(0);

  if (idx < candidates.length) {
    return (
      <img
        alt={`${name} logo`}
        src={candidates[idx]}
        className={`object-contain ${className}`}
        onError={() => {
          failedUrls.add(candidates[idx]);
          setIdx((i) => i + 1);
        }}
      />
    );
  }

  return (
    <div aria-label={`${name} logo`} className={`flex items-center justify-center bg-primary/10 ${className}`}>
      {fallbackIcon ? (
        <Icon name={fallbackIcon} size={14} className="text-primary" />
      ) : (
        <span className="text-xs font-bold text-primary">{initials(name)}</span>
      )}
    </div>
  );
}
